import React, { useState, useRef } from "react";
import { GraduationCap, FileSpreadsheet } from "lucide-react";
import CrudModule, { useEmployees } from "@/components/CrudModule";
import EadAdmin from "@/components/ead/EadAdmin";
import { PageHeader } from "@/components/CrudModule";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap as Cap, ListChecks, School, Users } from "lucide-react";
import TrainingsByEmployee from "@/components/ead/TrainingsByEmployee";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function TrainingImport({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const { canEdit } = useAuth();
  if (!canEdit) return null;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/trainings/import-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success(`Importado: ${data.created} novos, ${data.updated} atualizados, ${data.employees_created} colaborador(es) criado(s)`, { duration: 8000 });
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na importação");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} className="border-[#134074]/30 text-[#134074] hover:bg-[#134074]/5" data-testid="training-import-btn">
        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> {busy ? "Importando..." : "Importar planilha"}
      </Button>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="training-import-input" />
    </>
  );
}

function Registros() {
  const employees = useEmployees();
  const [k, setK] = useState(0);
  return (
    <CrudModule
      key={k}
      title="Controle de Treinamentos"
      subtitle="Capacitações, NRs e reciclagens por colaborador"
      icon={GraduationCap}
      endpoint="trainings"
      exportKey="trainings"
      testid="trainings"
      employees={employees}
      headerExtra={<TrainingImport onDone={() => setK((x) => x + 1)} />}
      columns={[
        { key: "date", label: "Data" },
        { key: "employee_name", label: "Colaborador" },
        { key: "topic", label: "Tema" },
        { key: "norm", label: "Norma" },
        { key: "workload_h", label: "Carga (h)" },
        { key: "instructor", label: "Instrutor" },
        { key: "valid_until", label: "Validade", badge: "validity" },
        { key: "status", label: "Status", badge: true },
        { key: "doc", label: "Certif.", file: true, idKey: "document_id" },
      ]}
      fields={[
        { name: "employee", label: "Colaborador", type: "employee", idKey: "employee_id", nameKey: "employee_name", full: true },
        { name: "topic", label: "Tema do treinamento", type: "text", required: true, full: true },
        { name: "norm", label: "Norma", type: "select", options: ["NR-01", "NR-05", "NR-06", "NR-11", "NR-12", "NR-20", "NR-23", "NR-33", "NR-34", "NR-35", "NR-10", "NR-13", "ISO 14001", "ISO 45001", "Geral"], default: "NR-01" },
        { name: "date", label: "Data", type: "date", required: true },
        { name: "valid_until", label: "Validade", type: "date" },
        { name: "workload_h", label: "Carga horária (h)", type: "number" },
        { name: "instructor", label: "Instrutor", type: "text" },
        { name: "status", label: "Status", type: "select", options: ["Válido", "Em Revisão", "Vencido"], default: "Válido" },
        { name: "document", label: "Certificado / lista (upload)", type: "file", idKey: "document_id", nameKey: "document_name", full: true },
      ]}
    />
  );
}

export default function Trainings() {
  const [colKey, setColKey] = useState(0);
  return (
    <div data-testid="trainings-page">
      <PageHeader icon={Cap} title="Treinamentos" subtitle="Registros de capacitação e a Escola Online (EAD) das NRs" />
      <Tabs defaultValue="colaboradores">
        <TabsList className="mb-4">
          <TabsTrigger value="colaboradores" data-testid="tab-colaboradores"><Users className="mr-1.5 h-4 w-4" /> Por Colaborador</TabsTrigger>
          <TabsTrigger value="registros" data-testid="tab-registros"><ListChecks className="mr-1.5 h-4 w-4" /> Todos os Registros</TabsTrigger>
          <TabsTrigger value="escola" data-testid="tab-escola"><School className="mr-1.5 h-4 w-4" /> Escola Online (EAD)</TabsTrigger>
        </TabsList>
        <TabsContent value="colaboradores">
          <TrainingsByEmployee key={colKey} headerExtra={<TrainingImport onDone={() => setColKey((x) => x + 1)} />} />
        </TabsContent>
        <TabsContent value="registros"><Registros /></TabsContent>
        <TabsContent value="escola"><EadAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
